// ─────────────────────────────────────────────────────────
// arquivos.js
// Upload e exibição de arquivos no Storage do Supabase self-hosted
// (gravado no Object Storage da Magalu — substitui o Cloudinary, Fase 9).
//
// Dois buckets:
//  - "arquivos" (privado): documentos de profissionais, fotos de feridas.
//    No banco guardamos uma referência "sb://arquivos/<caminho>", nunca um
//    link aberto. Na hora de exibir, geramos um link assinado temporário —
//    só funciona pra quem está logado.
//  - "publico": imagens sem dado sensível (banner, assinaturas e avatares do
//    INEP). Guardamos a URL pública normal, então quem só faz <img src={url}>
//    continua funcionando sem mudança.
//
// URLs antigas (Cloudinary, http/https) passam direto, sem alteração.
// ─────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';

const BUCKET_PRIVADO = 'arquivos';
const BUCKET_PUBLICO = 'publico';
const PREFIXO_REF = 'sb://';
const VALIDADE_LINK_S = 60 * 60; // 1h

// Pastas cujo conteúdo não é sensível e pode ficar em URL pública.
const PASTAS_PUBLICAS = ['inep/'];

function nomeSeguro(nome) {
  const limpo = String(nome || 'arquivo')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(-80);
  return limpo || 'arquivo';
}

/**
 * Envia um arquivo para o Storage.
 * @param {File} file
 * @param {string} folder - ex: "profissionais/abc123", "feridas", "inep/banners"
 * @returns {Promise<{url: string, publicId: string, nome: string}>}
 *   `url` é "sb://arquivos/..." (privado) ou a URL pública (pastas públicas).
 */
export async function uploadArquivo(file, folder = 'geral') {
  const pasta = String(folder || 'geral').replace(/^\/+|\/+$/g, '');
  const publico = PASTAS_PUBLICAS.some(p => `${pasta}/`.startsWith(p));
  const bucket = publico ? BUCKET_PUBLICO : BUCKET_PRIVADO;
  const aleatorio = Math.random().toString(36).slice(2, 8);
  const caminho = `${pasta}/${Date.now()}-${aleatorio}-${nomeSeguro(file.name)}`;

  const { error } = await supabase.storage.from(bucket).upload(caminho, file, {
    contentType: file.type || undefined,
    upsert: false,
  });
  if (error) throw new Error(error.message || 'Erro ao enviar arquivo');

  const url = publico
    ? supabase.storage.from(bucket).getPublicUrl(caminho).data.publicUrl
    : `${PREFIXO_REF}${bucket}/${caminho}`;

  return { url, publicId: caminho, nome: file.name };
}

export function isRefPrivada(url) {
  return typeof url === 'string' && url.startsWith(PREFIXO_REF);
}

// Cache dos links assinados, pra não pedir um novo a cada renderização.
const cacheLinks = new Map(); // ref -> { url, expiraEm }

/**
 * Converte uma referência "sb://bucket/caminho" num link temporário.
 * Qualquer outra URL volta igual.
 */
export async function resolverArquivoUrl(url) {
  if (!isRefPrivada(url)) return url;
  const agora = Date.now();
  const emCache = cacheLinks.get(url);
  if (emCache && emCache.expiraEm - agora > 5 * 60 * 1000) return emCache.url;

  const resto = url.slice(PREFIXO_REF.length);
  const barra = resto.indexOf('/');
  const bucket = resto.slice(0, barra);
  const caminho = resto.slice(barra + 1);
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(caminho, VALIDADE_LINK_S);
  if (error || !data?.signedUrl) throw new Error(error?.message || 'Arquivo indisponível');

  cacheLinks.set(url, { url: data.signedUrl, expiraEm: agora + VALIDADE_LINK_S * 1000 });
  return data.signedUrl;
}

/** Hook: devolve a URL pronta pra uso (ou null enquanto resolve).
 *  Renova o link antes de vencer, pra tela que fica aberta por horas. */
export function useArquivoUrl(url) {
  const [resolvida, setResolvida] = useState(() => (isRefPrivada(url) ? null : url));
  useEffect(() => {
    let ativo = true;
    if (!isRefPrivada(url)) { setResolvida(url); return undefined; }
    setResolvida(null);
    const resolver = () => resolverArquivoUrl(url)
      .then(u => { if (ativo) setResolvida(u); })
      .catch(() => { if (ativo) setResolvida(null); });
    resolver();
    // 4 min antes de vencer: o cache só devolve link com mais de 5 min de folga, então gera um novo
    const timer = setInterval(resolver, (VALIDADE_LINK_S - 4 * 60) * 1000);
    return () => { ativo = false; clearInterval(timer); };
  }, [url]);
  return resolvida;
}

/** <img> que aceita tanto URL normal quanto referência privada. */
export function ArquivoImg({ src, alt = '', ...props }) {
  const url = useArquivoUrl(src);
  if (!url) return <span style={{ display: 'inline-block', ...(props.style || {}), background: '#f1f5f9' }} title={alt} />;
  return <img src={url} alt={alt} {...props} />;
}

/** <a> que aceita tanto URL normal quanto referência privada. */
export function ArquivoLink({ href, children, ...props }) {
  const url = useArquivoUrl(href);
  return (
    <a
      href={url || '#'}
      {...props}
      onClick={e => {
        if (props.onClick) props.onClick(e);
        if (!isRefPrivada(href)) { if (!url) e.preventDefault(); return; }
        // Referência privada: sempre pede um link válido na hora do clique
        // (o href pode ter vencido se a tela ficou aberta muito tempo).
        e.preventDefault();
        if (props.target === '_blank') { abrirArquivo(href); return; }
        resolverArquivoUrl(href)
          .then(u => { window.location.href = u; })
          .catch(err => alert('Não foi possível abrir o arquivo: ' + err.message));
      }}
    >
      {children}
    </a>
  );
}

/** Abre um arquivo numa nova aba (resolve a referência privada antes). */
export async function abrirArquivo(url) {
  // Abre a aba já no clique (senão o navegador bloqueia como pop-up) e só
  // depois aponta pro link assinado.
  const aba = window.open('', '_blank');
  try {
    const final = await resolverArquivoUrl(url);
    if (aba) aba.location.href = final; else window.location.href = final;
  } catch (e) {
    if (aba) aba.close();
    alert('Não foi possível abrir o arquivo: ' + e.message);
  }
}
